import { TicketLineDTO, TicketDivisionDTO, InvoiceParamsFromPosDTO, InvoiceDTO, BuyerCustomerDTO, Res } from 'core/api';
import { Result, CustomError, } from 'core/infrastructure';
import { makeTicketReadyToClose, showCloseDocumentScreen } from 'pos/modules/helpers/document-creation';
import * as PartialRefundStore from './PartialRefundStore';
import { Message } from 'core/ui-next/modal';

export type TicketLineDivision = {
	line: TicketLineDTO;
	leftQty: Nullable<number>;
	rightQty: Nullable<number>;
};

export type TicketDivision = { invoice: InvoiceDTO, division: TicketDivisionDTO, lines: TicketLineDivision[] }

function createLineDivisions(division: TicketDivisionDTO): TicketLineDivision[] {

	const lines: TicketLineDivision[] = PartialRefundStore.getOriginalTicket().Lines.map(line => {

		const leftLine = division.Left.Lines.find(l => l.Id === line.Id);
		const rightLine = division.Right.Lines.find(l => l.Id === line.Id);

		return {
			line,
			leftQty: leftLine ? leftLine.Quantity : null,
			rightQty: rightLine ? rightLine.Quantity : null
		};
	});

	return lines;
}

export async function init(invoice: InvoiceDTO): Promise<Result<TicketDivision, CustomError>> {

	const result = await PartialRefundStore.createDivision(invoice);
	if (!result.ok) {
		return Result.error(result.error);
	}

	return Result.ok({
		invoice,
		division: result.value,
		lines: createLineDivisions(result.value)
	});
}

export async function moveOneToLeft(status: TicketDivision, line: TicketLineDTO): Promise<TicketDivision> {

	const lineDivision = status.lines.find(x => x.line.Id === line.Id)!;
	// Si no puedo mover más cantidad no tengo nada más que hacer
	if (!lineDivision.rightQty)
		return status;

	const result = await PartialRefundStore.moveOneUnitToLeft(status.division, { ...line, Quantity: lineDivision.rightQty });
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

export async function moveOneToRight(status: TicketDivision, line: TicketLineDTO): Promise<TicketDivision> {

	const lineDivision = status.lines.find(x => x.line.Id === line.Id)!;
	// Si no puedo mover más cantidad no tengo nada más que hacer
	if (!lineDivision.leftQty)
		return status;

	const result = await PartialRefundStore.moveOneUnitToRight(status.division, { ...line, Quantity: lineDivision.leftQty });
	if (!result.ok) {
		return status;
	}

	return { ...status, division: result.value, lines: createLineDivisions(result.value) };
}

const refundClosingTicket = async (invoice: InvoiceDTO) => {

	const result = await makeTicketReadyToClose({
		closeAs: 'Refund',
		getTicket: PartialRefundStore.getClosingTicket,
		setFriendlyName: 'DISABLE_ASK_FOR_FRIENDLY_NAME',
		setTableCompanions: 'DISABLE_SET_TABLE_COMPANIONS'
	});

	if (!result.ok)
		return Result.error();

	return await showCloseDocumentScreen({
		layout: 'CreateRefund',
		setCustomer: async (_customer: BuyerCustomerDTO) => { await Message.show(Res.Msg.MSG_CANNOT_SET_CUSTOMER_FOR_PARTIAL_REFUND()); },
		getDocument: () => {
			const ticket = PartialRefundStore.getClosingTicket();
			return {
				ticketIds: [ticket.Id],
				ticketGlobalId: ticket.GlobalId,
				customer: ticket.Customer,
				pendingAmount: ticket.PendingAmount,
				discounts: {
					cashDiscount: ticket.CashDiscount,
					discountPercentageInCash: ticket.DiscountPercentageInCash,
					discountPercentage: ticket.DiscountPercentage
				}
			};
		},
		closeDocument: async (params: InvoiceParamsFromPosDTO) => {
			params.RelatedInvoiceId = invoice.Id;
			return PartialRefundStore.refundClosingTicket(params);
		}
	});
};


export async function refundTicket(status: TicketDivision): Promise<Result> {

	const beginClosingResult = await PartialRefundStore.beginClosingTicket(
		status.division.ClosingQuantities,
		status.division.Right.TableCompanions);

	if (!beginClosingResult.ok) {
		return beginClosingResult;
	}

	return await refundClosingTicket(status.invoice);
}


export async function cancel() {
	await PartialRefundStore.cancelClosingTicket();
	await PartialRefundStore.cancelOriginalTicket();
}

